import type {
  Award,
  Certification,
  CustomSection,
  CustomSectionGroup,
  Education,
  Experience,
  Interest,
  Language,
  Profile,
  Project,
  Publication,
  Reference,
  SectionWithItem,
  Skill,
  Volunteer,
} from "@/lib/schema/sections";
import { Link as LinkIcon, Mail, MapPin, Phone } from "lucide-react";

import AccountIcon from "@/components/AccountIcon";
import { TResume } from "@/lib/store/resume";
import { onPreviewSectionClick } from "@/lib/utils/resumeBuilder";
import { hexToRgb, isUrl } from "@/utils";
import { isEmptyString } from "@/utils/preview";
import classNames from "classnames";
import { get } from "lodash";
import { Fragment, ReactNode } from "react";
import { TemplateProps } from ".";
import { LevelMap } from "../sidebars/left/sections/Languages";
import { Picture } from "../sidebars/left/sections/shared/Picture";

const Header = ({ resume }: { resume: TResume }) => {
  const basics = resume.data.basics;

  if (!basics) return null;

  return (
    <div
      className="mb-[30px] flex items-center"
      onClick={() =>
        onPreviewSectionClick(`builder-basics`, {
          highlight: false,
          accordion: false,
        })
      }
    >
      <Picture resume={resume} className="mr-[44px]" />
      <div className="flex h-[120px] flex-col justify-between">
        <div className="mb-4 text-3xl font-bold">
          {basics.name}
          {basics.headline && (
            <span className="text-2xl font-bold"> - {basics.headline}</span>
          )}
        </div>
        <div className="text-md flex flex-wrap items-center  space-y-1">
          {basics.location && (
            <div className="mr-2 flex items-center gap-x-1.5">
              <MapPin
                className="resume-icon text-resume-primary"
                strokeWidth={2.5}
                size={16}
              />
              <div>{basics.location}</div>
            </div>
          )}
          {basics.phone && (
            <div className="mr-2 flex items-center gap-x-1.5">
              <Phone
                className="resume-icon text-resume-primary"
                strokeWidth={2.5}
                size={16}
              />
              <a href={`tel:${basics.phone}`} target="_blank" rel="noreferrer">
                {basics.phone}
              </a>
            </div>
          )}
          {basics.email && (
            <div className="mr-2 flex items-center gap-x-1.5">
              <Mail
                className="resume-icon text-resume-primary"
                strokeWidth={2.5}
                size={16}
              />
              <a
                href={`mailto:${basics.email}`}
                target="_blank"
                rel="noreferrer"
              >
                {basics.email}
              </a>
            </div>
          )}
          {basics.customFields?.map((item) => (
            <div key={item.id} className="mr-2 flex items-center gap-x-1.5">
              <AccountIcon
                name={item.icon}
                className="resume-icon text-resume-primary"
                strokeWidth={2.5}
                size={16}
              />
              <span>{[item.name, item.value].filter(Boolean).join(": ")}</span>
            </div>
          ))}
          {basics.website?.href && (
            <div className="mr-2 flex items-center gap-x-1.5">
              <LinkIcon
                className="resume-icon text-resume-primary"
                strokeWidth={2.5}
                size={16}
              />
              <a target="_blank" rel="noreferrer">
                {basics.website?.href}
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Summary = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.summary;

  if (!section?.visible || isEmptyString(section?.content)) return null;

  const primaryColor = resume.data.settings?.theme.primary;

  return (
    <section
      id={section?.id}
      className="section-item flex items-baseline"
      onClick={() => onPreviewSectionClick(`builder-${section?.id}`)}
    >
      {/* <h4 className="mb-2  w-[200px] py-1 text-base font-bold text-black">
        {section?.name}
      </h4> */}

      <div className="mr-[24px] w-[120px] flex-shrink-0  text-left text-xs"></div>

      <div
        className="border-resume-primary  flex-1 border-l-1 pb-[40px] pl-[20px]"
        style={{ borderColor: primaryColor && hexToRgb(primaryColor, 0.4) }}
      >
        <h4 className="pb-[5px] text-lg !font-extrabold text-black">
          {section?.name}
        </h4>
        <div className="relative">
          <div
            dangerouslySetInnerHTML={{ __html: section?.content || "" }}
            // className="border-resume-primary  flex-1 border-l-1 py-[20px] pl-[20px]"
          />
          <div className="bg-resume-primary absolute left-[-24px] top-[5px] h-[8px] w-[8px] rounded-full group-[.main]:block" />
        </div>
      </div>
    </section>
  );
};

type RatingProps = { level: number };

const Rating = ({ level }: RatingProps) => (
  <div className="flex items-center gap-x-1.5">
    {Array.from({ length: 5 }).map((_, index) => (
      <div
        key={index}
        className={classNames(
          "border-resume-primary h-2 w-10 border-1",
          level > index && "bg-resume-primary",
        )}
      />
    ))}
  </div>
);

type SectionProps<T> = {
  section?: SectionWithItem<T> | CustomSectionGroup;
  children?: (item: T) => React.ReactNode;
  className?: string;
  urlKey?: keyof T;
  levelKey?: keyof T;
  summaryKey?: keyof T;
  keywordsKey?: keyof T;
  leftRender?: (item: T) => ReactNode;
  rightRender?: (item: T) => ReactNode;
  resume?: TResume;
};

const Section = <T,>({
  section,
  children,
  className,
  urlKey,
  levelKey,
  summaryKey,
  keywordsKey,
  leftRender,
  rightRender,
  resume,
}: SectionProps<T>) => {
  if (!section?.visible || !section?.items?.length) return null;
  const primaryColor = resume?.data.settings?.theme.primary;

  return (
    <section
      id={section.id}
      className="section-item flex items-baseline gap-6"
      onClick={() =>
        onPreviewSectionClick(`builder-${section?.id}-${section?.items[0]?.id}`)
      }
    >
      <div
        className="grid w-full gap-x-6"
        style={{ gridTemplateColumns: `repeat(1, 1fr)` }}
      >
        {section.items
          .filter((item) => item.visible)
          .map((item, i) => {
            const url = urlKey && get(item, urlKey);
            const level = LevelMap[levelKey && get(item, levelKey, "basic")];

            const summary = (summaryKey && get(item, summaryKey, "")) as
              | string
              | undefined;
            const keywords = (keywordsKey && get(item, keywordsKey, [])) as
              | string[]
              | undefined;
            return (
              <div
                className="flex items-baseline justify-between"
                key={item.id}
                onClick={(e) => {
                  e.stopPropagation();
                  onPreviewSectionClick(`builder-${section?.id}-${item.id}`);
                }}
              >
                <div
                  className={classNames(
                    "mr-[24px] w-[120px] text-right  text-xs",
                    leftRender ? "pb-[40px]" : "",
                  )}
                >
                  {i === 0 && (
                    <h4 className="pb-[8px] text-lg !font-extrabold text-black opacity-0">
                      {section.name}
                    </h4>
                  )}
                  {leftRender?.(item as any)}
                </div>

                <div
                  className="border-resume-primary  flex-1 border-l-1 pb-[40px] pl-[20px]"
                  style={{
                    borderColor: primaryColor && hexToRgb(primaryColor, 0.4),
                  }}
                >
                  {i === 0 && (
                    <h4 className="pb-[5px] text-lg !font-extrabold text-black">
                      {section.name}
                    </h4>
                  )}
                  <div className="relative space-y-1">
                    {rightRender?.(item as any) && rightRender?.(item as any)}
                    {url?.href && (
                      <a target="_blank" rel="noreferrer" href={url?.href}>
                        {url?.href}
                      </a>
                    )}
                    {level !== undefined && level > 0 && (
                      <Rating level={level} />
                    )}

                    {keywords !== undefined && keywords.length > 0 && (
                      <p className="text-sm">{keywords.join(", ")}</p>
                    )}

                    {summary !== undefined && !isEmptyString(summary) && (
                      <div dangerouslySetInnerHTML={{ __html: summary }} />
                    )}
                    <div className="bg-resume-primary absolute left-[-24px] top-[8px] h-[8px] w-[8px] rounded-full group-[.main]:block" />
                  </div>
                </div>
              </div>
            );
          })}
      </div>
    </section>
  );
};

const Profiles = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.profiles;
  const fontSize = resume.data.settings?.typography.font.size;

  const rightRender = (item: Profile) => {
    return (
      <>
        {isUrl(item.url?.href) ? (
          <a href={item.url?.href} className="text-inherit">
            <img
              className="resume-icon mr-1"
              width={fontSize}
              height={fontSize}
              alt={item.network}
              src={`https://cdn.simpleicons.org/${item.icon}`}
            />
            {item.username}
          </a>
        ) : (
          <div>{item.username}</div>
        )}
        {item.url?.href && (
          <a
            className="block text-sm"
            target="_blank"
            rel="noreferrer"
            href={item.url?.href}
          >
            {item.url?.href}
          </a>
        )}
      </>
    );
  };

  return (
    <Section<Profile>
      resume={resume}
      section={section}
      rightRender={rightRender}
    ></Section>
  );
};

const Experience = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.workExperience;
  const leftRender = (item: Experience) => {
    return (
      <>
        <div>{item.date}</div>
        {item.location && <div>{item.location}</div>}
      </>
    );
  };

  const rightRender = (item: Experience) => {
    return (
      <>
        {item.position && (
          <div className="text-base font-extrabold ">{item.position}</div>
        )}

        <div className="flex items-center justify-between">
          <div className="text-base font-extrabold">{item.company}</div>
        </div>
      </>
    );
  };

  return (
    <Section<Experience>
      section={section}
      urlKey="url"
      summaryKey="summary"
      leftRender={leftRender}
      rightRender={rightRender}
      resume={resume}
    ></Section>
  );
};

const Internship = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.internship;

  const leftRender = (item: Experience) => {
    return (
      <>
        <div>{item.date}</div>
        {item.location && <div>{item.location}</div>}
      </>
    );
  };

  const rightRender = (item: Experience) => {
    return (
      <>
        {item.position && (
          <div className="text-base font-extrabold ">{item.position}</div>
        )}

        <div className="flex items-center justify-between">
          <div className="text-base font-extrabold">{item.company}</div>
        </div>
      </>
    );
  };

  return (
    <Section<Experience>
      section={section}
      urlKey="url"
      summaryKey="summary"
      leftRender={leftRender}
      rightRender={rightRender}
      resume={resume}
    ></Section>
  );
};

const Education = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.education;

  const leftRender = (item: Education) => {
    return (
      <>
        <div>{item.date}</div>
      </>
    );
  };

  const rightRender = (item: Education) => {
    return (
      <>
        <div className="flex items-center justify-between text-base font-extrabold">
          <div className="flex">
            {item.major && <div>{item.major}</div>}
            {item.studyType && <div>{` | ${item.studyType}`}</div>}
          </div>
          {item.score && <div>{item.score}</div>}
        </div>
        <div className="text-base font-extrabold">{item.institution}</div>

        {item.officialSite && <div>{item.officialSite}</div>}
      </>
    );
  };

  return (
    <Section<Education>
      section={section}
      summaryKey="summary"
      leftRender={leftRender}
      rightRender={rightRender}
      resume={resume}
    ></Section>
  );
};

const Awards = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.awards;
  const leftRender = (item: Award) => {
    return (
      <>
        <div>{item.date}</div>
      </>
    );
  };

  const rightRender = (item: Award) => {
    return (
      <div className="flex justify-between text-base font-extrabold">
        <div className="text-base">{item.title}</div>
        <div>{item.awarder}</div>
      </div>
    );
  };

  return (
    <Section<Award>
      section={section}
      urlKey="url"
      summaryKey="summary"
      leftRender={leftRender}
      rightRender={rightRender}
      resume={resume}
    ></Section>
  );
};

const Certifications = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.certifications;

  const leftRender = (item: Certification) => {
    return (
      <>
        <div>{item.date}</div>
      </>
    );
  };

  const rightRender = (item: Certification) => {
    return (
      <div className="flex justify-between text-base font-bold">
        <div>{item.name}</div>
        <div>{item.issuer}</div>
      </div>
    );
  };

  return (
    <Section<Certification>
      section={section}
      urlKey="url"
      summaryKey="summary"
      leftRender={leftRender}
      rightRender={rightRender}
      resume={resume}
    ></Section>
  );
};

const Skills = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.skills;
  const leftRender = (item: Skill) => {
    return <div>{item.name}</div>;
  };

  const rightRender = (item: Skill) => {
    return (
      <>
        <div className="flex items-center justify-between">
          <div className="text-base font-extrabold">{item.description}</div>
          {/* <div className="text-base font-bold">{item.name}</div> */}
        </div>
      </>
    );
  };

  return (
    <Section<Skill>
      section={section}
      levelKey="level"
      keywordsKey="keywords"
      rightRender={rightRender}
      leftRender={leftRender}
      resume={resume}
    ></Section>
  );
};

const Interests = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.interests;

  const rightRender = (item: Interest) => {
    return (
      <>
        <div className="text-base font-extrabold">{item.name}</div>
      </>
    );
  };

  return (
    <Section<Interest>
      section={section}
      keywordsKey="keywords"
      className="space-y-0.5"
      rightRender={rightRender}
      resume={resume}
    ></Section>
  );
};

const Publications = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.publications;
  const leftRender = (item: Publication) => {
    return (
      <>
        <div>{item.date}</div>
      </>
    );
  };

  const rightRender = (item: Publication) => {
    return (
      <div className="flex justify-between text-base font-extrabold">
        <div>{item.name}</div>
        <div>{item.publisher}</div>
      </div>
    );
  };
  return (
    <Section<Publication>
      section={section}
      urlKey="url"
      summaryKey="summary"
      leftRender={leftRender}
      rightRender={rightRender}
      resume={resume}
    ></Section>
  );
};

const Volunteer = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.volunteer;

  const leftRender = (item: Volunteer) => {
    return (
      <>
        <div>{item.date}</div>
        <div>{item.location}</div>
      </>
    );
  };

  const rightRender = (item: Volunteer) => {
    return (
      <>
        <div className="flex justify-between text-base font-extrabold">
          <div>{item.organization}</div>
        </div>
        <div>{item.position}</div>
      </>
    );
  };

  return (
    <Section<Volunteer>
      section={section}
      urlKey="url"
      summaryKey="summary"
      leftRender={leftRender}
      rightRender={rightRender}
      resume={resume}
    ></Section>
  );
};

const Languages = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.languages;

  const rightRender = (item: Language) => {
    return (
      <>
        <div className="text-base font-extrabold">{item.name}</div>
        <div>{item.description}</div>
      </>
    );
  };

  return (
    <Section<Language>
      section={section}
      levelKey="level"
      rightRender={rightRender}
      resume={resume}
    ></Section>
  );
};

const Projects = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.projects;

  const leftRender = (item: Project) => {
    return (
      <>
        <div>{item.date}</div>
      </>
    );
  };

  const rightRender = (item: Project) => {
    return (
      <div className="flex justify-between text-base font-extrabold">
        <div>{item.name}</div>
        <div>{item.description}</div>
      </div>
    );
  };
  return (
    <Section<Project>
      section={section}
      urlKey="url"
      summaryKey="summary"
      keywordsKey="keywords"
      leftRender={leftRender}
      rightRender={rightRender}
      resume={resume}
    ></Section>
  );
};

const References = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.references;

  const rightRender = (item: Reference) => {
    return (
      <>
        <div className="text-base font-extrabold">{item.name}</div>
        <div>{item.description}</div>
      </>
    );
  };
  return (
    <Section<Reference>
      section={section}
      urlKey="url"
      summaryKey="summary"
      rightRender={rightRender}
      resume={resume}
    ></Section>
  );
};

const Custom = ({ id, resume }: { id: string; resume: TResume }) => {
  const section = resume.data.sections?.custom?.[id];

  const leftRender = (item: CustomSection) => {
    return (
      <>
        <div>{item.date}</div>
      </>
    );
  };

  const rightRender = (item: CustomSection) => {
    if (!item.name && !item.location && !item.description) {
      return null;
    }
    return (
      <>
        <div className="flex justify-between text-base font-extrabold">
          <div>{item.name}</div>
          <div>{item.location}</div>
        </div>
        <div>{item.description}</div>
      </>
    );
  };
  return (
    <Section<CustomSection>
      section={section}
      urlKey="url"
      summaryKey="summary"
      keywordsKey="keywords"
      leftRender={leftRender}
      rightRender={rightRender}
      resume={resume}
    ></Section>
  );
};

const mapSectionToComponent = (section: any, resume: TResume) => {
  switch (section) {
    case "profiles":
      return <Profiles resume={resume} />;
    case "summary":
      return <Summary resume={resume} />;
    case "workExperience":
      return <Experience resume={resume} />;
    case "internship":
      return <Internship resume={resume} />;
    case "education":
      return <Education resume={resume} />;
    case "awards":
      return <Awards resume={resume} />;
    case "certifications":
      return <Certifications resume={resume} />;
    case "skills":
      return <Skills resume={resume} />;
    case "interests":
      return <Interests resume={resume} />;
    case "publications":
      return <Publications resume={resume} />;
    case "volunteer":
      return <Volunteer resume={resume} />;
    case "languages":
      return <Languages resume={resume} />;
    case "projects":
      return <Projects resume={resume} />;
    case "references":
      return <References resume={resume} />;
    default:
      if (section.startsWith("custom."))
        return <Custom id={section.split(".")[1]} resume={resume} />;

      return null;
  }
};

export const Vegan = ({
  resume,
  columns,
  isFirstPage = false,
}: TemplateProps) => {
  const [main, sidebar] = columns;

  return (
    <div className="p-custom">
      {isFirstPage && <Header resume={resume} />}

      {main.map((section) => (
        <Fragment key={section}>
          {mapSectionToComponent(section, resume)}
        </Fragment>
      ))}

      {sidebar.map((section) => (
        <Fragment key={section}>
          {mapSectionToComponent(section, resume)}
        </Fragment>
      ))}
    </div>
  );
};
